import { ApolloError } from '@apollo/client';
import { permissionsByEntity, useCheckPermissions } from '@cmg/auth';
import { Alert } from '@cmg/design-system';
import React from 'react';

import Banner from '../../../../common/components/indicators/banner/Banner';
import Loading from '../../../../common/components/indicators/loading/Loading';
import ServerErrorsBanner from '../../../../common/components/indicators/server-error/ServerErrorsBanner';
import FlexLayout from '../../../../common/components/layout/flex-layout/FlexLayout';
import NotAccessible from '../../../../common/components/layout/not-accessible/NotAccessible';
import { useFeatureToggles } from '../../../../common/config';
import { OfferingType, OrderBookState } from '../../../../graphql';
import InternalDemandSummary from '../../components/internal-demand-summary/InternalDemandSummary';
import { useSideBar_OfferingDetails_Offering_BuySideQuery } from '../../components/side-bar/graphql';
import { OfferingDetailsLayout } from '../../offering-details/components/OfferingDetailsLayout';
import { NoRelationshipOrderbookLegalBanner } from '../../SNoRelationshipBanners';
import isManagerRelationshipError from '../../utils/isManagerRelationshipError';
import ManageBuySideIndication from './components/manage-indication/ManageBuySideIndication';
import { NoticesAndDisclaimers } from './components/notices-and-disclaimers/NoticesAndDisclaimers';
import { BuySideIndicationQuery } from './context/hooks/buy-side/__generated__';
import { useIndicationActivityBuySideContext } from './context/IndicationActivityBuySideContext';

/**
 * Main entry point for the Buy Side Indication Activity Side Panel
 * route.  Determines whether to show the view for managing an
 * existing indication or creating an indication.
 */

const isAuthNotAuthorized144AError = (error: ApolloError, isReg144A: boolean): boolean => {
  // We assume that any AUTH_NOT_AUTHORIZED in the context of buyside refers to missing relationship with managers
  // this might be improved in future with check against the explicit code or subcode
  // e.g. gqlError.extensions?.subcode === 'BUYSIDE_NO_RELATIONSHIP_WITH_MANAGER'
  return (
    !!error?.graphQLErrors?.some(gqlError => gqlError.extensions?.code === 'AUTH_NOT_AUTHORIZED') &&
    isReg144A
  );
}; // Adjust the import path as needed

const shouldShowNoRelationshipBanner = (
  offering: BuySideIndicationQuery['basicPublishedOffering'] | undefined,
  error: ApolloError | undefined,
  hasNoRelationship: boolean
): boolean => {
  if (!offering && !error) return false;
  return (
    (!!offering && !error && hasNoRelationship) || (!!error && isManagerRelationshipError(error))
  );
};

const IndicationActivityBuySide: React.FC = () => {
  const {
    basicPublishedOffering,
    loading,
    error,
    complianceWarning: { hasComplianceWarning },
    hasActiveRelationship,
    offeringId,
    access,
    isReg144A,
  } = useIndicationActivityBuySideContext();
  const hasMDLIOIPermissions = useCheckPermissions([permissionsByEntity.Ioi.READ]);
  const { isOdsFunctionalityEnabled } = useFeatureToggles();

  const { data } = useSideBar_OfferingDetails_Offering_BuySideQuery({
    variables: { offeringId },
  });
  const [publishedOffering] = data?.publishedOfferings?.items ?? [];
  const isLinkedOffering = !!publishedOffering?.dataLabFull?.linkedOfferingId;
  const shouldShowInternalDemand = isLinkedOffering || !isOdsFunctionalityEnabled;

  const indicationExists = !!basicPublishedOffering?.myInstitutionalIndication;
  const orderBookIsClosed =
    basicPublishedOffering?.publicOrderBook?.state === OrderBookState.Closed;

  const hasNoRelationship = !hasActiveRelationship;

  const isOfferingExecutingOnPlatform =
    basicPublishedOffering?.orderEntryProfile?.isExecutingOnPlatform;

  if (loading) {
    return <Loading />;
  }

  if (!access.canReadOfferingAndIndication) {
    return (
      <NotAccessible
        subject="Indication"
        content="You are not permitted to access this Indication"
      />
    );
  }

  if (error && isAuthNotAuthorized144AError(error, isReg144A)) {
    return (
      <OfferingDetailsLayout>
        <Alert severity="warning">
          You do not have the ability to view the Offering Details because we are unable to verify
          that you have a relationship with one or more active bookrunners on this offering, or you
          do not have a valid QIB certificate on file with CMG.{' '}
          <div>
            Please contact <strong>support@cmgx.io </strong>
            for assistance.
          </div>
        </Alert>
      </OfferingDetailsLayout>
    );
  }

  // Basically we have two scenarios, when a user have no relationship with an active bookrunner and with the sellside in both we want to show the
  // warning banner and hide the error.
  const showNoRelationshipBanner = shouldShowNoRelationshipBanner(
    basicPublishedOffering,
    error,
    hasNoRelationship
  );
  const isNoRelationshipBannerVisible = showNoRelationshipBanner || hasNoRelationship;
  const shouldHideServerErrors = isNoRelationshipBannerVisible;

  const isInternalSummaryVisible =
    !!basicPublishedOffering &&
    hasMDLIOIPermissions &&
    !hasComplianceWarning &&
    !isNoRelationshipBannerVisible &&
    shouldShowInternalDemand;

  const offeringType = basicPublishedOffering?.type;
  const displayDisclaimerBanner =
    offeringType !== OfferingType.UnregisteredBlock &&
    isOfferingExecutingOnPlatform &&
    !isNoRelationshipBannerVisible &&
    (indicationExists || !orderBookIsClosed);

  return (
    <FlexLayout>
      {!shouldHideServerErrors && error && <ServerErrorsBanner error={error} />}

      {!isNoRelationshipBannerVisible && orderBookIsClosed && (
        <Banner variant="information">
          {indicationExists
            ? 'The order book is closed. Please contact your sell side representative if changes are needed.'
            : 'The order book is closed. Please contact your sell side representative if you have interest in participating.'}
        </Banner>
      )}

      {isInternalSummaryVisible && <InternalDemandSummary offeringId={offeringId} />}

      {basicPublishedOffering && hasActiveRelationship && (
        <ManageBuySideIndication isOrderBookOpen={!orderBookIsClosed} />
      )}

      {displayDisclaimerBanner && (
        <NoticesAndDisclaimers disclaimers={basicPublishedOffering?.disclaimers} />
      )}

      {isNoRelationshipBannerVisible && <NoRelationshipOrderbookLegalBanner />}
    </FlexLayout>
  );
};

export default React.memo(IndicationActivityBuySide);
